<script setup lang="ts">
/**
 * design-system.md -> "Dialog"; design/screens/create-tenant-dialog.html. 04-tenants.md ->
 * "Create tenant dialog". Mounted once in AppShell, bound to session.createTenantOpen — the rail
 * "+" and this page's "New tenant" button both just flip that ref.
 *
 * Sending domain stays REQUIRED here (no "Optional" marker, unlike the screen) — the server
 * rejects a tenant without one; see IMPLEMENTATION_PLAN.md §7.4 and the tenants engineer's report
 * for the deviation.
 */
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { post } from '@/api';
import { refreshTenants } from '@/session';
import type { Tenant } from '@/types';
import Dialog from '@/components/ui/Dialog.vue';
import Field from '@/components/ui/Field.vue';
import TextInput from '@/components/ui/TextInput.vue';
import Button from '@/components/ui/Button.vue';

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

const router = useRouter();

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const name = ref('');
const slug = ref('');
const domain = ref('');
// Once the user types into the slug field directly, the name -> slug auto-fill stops (04-tenants.md).
const slugEdited = ref(false);

const busy = ref(false);
const nameError = ref<string>();
const slugError = ref<string>();
const domainError = ref<string>();
const formError = ref<string>();

/** Transliterate a tenant name into a slug: å/ä -> a, ö -> o, é -> e, everything else outside
 * a-z0-9 collapses to a single dash, with leading/trailing dashes trimmed. */
function deriveSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[åä]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/é/g, 'e')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

watch(name, value => {
  if (!slugEdited.value) slug.value = deriveSlug(value);
});

/** Typing directly into the slug field only ever allows lowercase letters, numbers and dashes —
 * anything else is dropped rather than blocking the keystroke. */
function onSlugInput(value: string) {
  slugEdited.value = true;
  slug.value = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
}

const canSubmit = computed(
  () => !!name.value.trim() && !!domain.value.trim() && SLUG_PATTERN.test(slug.value)
);

function resetForm() {
  name.value = '';
  slug.value = '';
  domain.value = '';
  slugEdited.value = false;
  busy.value = false;
  nameError.value = undefined;
  slugError.value = undefined;
  domainError.value = undefined;
  formError.value = undefined;
}

// Reset whenever the dialog closes — Cancel, x, Escape, or a successful create — so the next open
// never shows a stale name/slug/domain or a leftover error.
watch(
  () => props.open,
  isOpen => {
    if (!isOpen) resetForm();
  }
);

async function submit() {
  if (!canSubmit.value || busy.value) return;

  busy.value = true;
  nameError.value = undefined;
  slugError.value = undefined;
  domainError.value = undefined;
  formError.value = undefined;

  try {
    const tenant = await post<Tenant>('/api/admin/tenants', {
      slug: slug.value,
      name: name.value.trim(),
      domain: domain.value.trim()
    });

    emit('close');
    await refreshTenants();
    await router.push({ name: 'tenant-keys', params: { slug: tenant.slug } });
  } catch (e) {
    const message = (e as Error).message;
    if (message.includes('already exists')) {
      slugError.value = message;
    } else if (message.includes('already in use')) {
      domainError.value = message;
    } else {
      formError.value = message;
    }
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <Dialog
    :open="open"
    overline="New tenant"
    title="Create a tenant"
    intro="A tenant gets its own API keys, webhooks and event log. You can issue its first key right after."
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4.5">
      <Field id="nt-name" label="Name" :error="nameError">
        <template #default="{ id, describedBy, invalid }">
          <TextInput
            :id="id"
            v-model="name"
            placeholder="Tenant name"
            :invalid="invalid"
            :aria-describedby="describedBy"
            :disabled="busy"
            @keydown.enter="submit"
          />
        </template>
      </Field>

      <Field
        id="nt-slug"
        label="Slug"
        hint="Lowercase letters, numbers and dashes. Filled in from the name until you edit it."
        :error="slugError"
      >
        <template #default="{ id, describedBy, invalid }">
          <TextInput
            :id="id"
            :model-value="slug"
            mono
            placeholder="tenant-slug"
            :invalid="invalid"
            :aria-describedby="describedBy"
            :disabled="busy"
            @update:model-value="onSlugInput"
            @keydown.enter="submit"
          />
        </template>
      </Field>

      <Field id="nt-domain" label="Sending domain" :error="domainError">
        <template #default="{ id, describedBy, invalid }">
          <TextInput
            :id="id"
            v-model="domain"
            placeholder="mail.example.com"
            :invalid="invalid"
            :aria-describedby="describedBy"
            :disabled="busy"
            @keydown.enter="submit"
          />
        </template>
      </Field>

      <p v-if="formError" class="type-caption text-danger-fg">{{ formError }}</p>
    </div>

    <template #footer>
      <Button variant="secondary" :disabled="busy" @click="emit('close')">Cancel</Button>
      <Button variant="primary" :disabled="!canSubmit || busy" @click="submit">
        {{ busy ? 'Creating…' : 'Create tenant' }}
      </Button>
    </template>
  </Dialog>
</template>
